import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { NodeStatus } from '../../../types/artifactory';
import { HydrationNodeRow } from './HydrationNodeRow';
import { nodePathResolver } from '../../../utils/nodePathResolver';
import { FiX } from 'react-icons/fi';

interface HydrationPlanTableProps {
  hydrationPlan: any;
  selectedNodes: string[];
  currentlyHydrating: string | null;
  completedNodes: Set<string>;
  failedNodes: Set<string>;
  nodeStatuses: Record<string, NodeStatus>;
  nodeErrors: Record<string, string>;
  isAutoHydrating: boolean;
  manifest: any;
  onSelectNode: (nodeId: string) => void;
  onHydrateNode: (nodeId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  maxHeight?: string;
  onFocusNodeInGraph?: (nodeId: string) => void;
  isRootNodeView?: boolean;
  searchTerm?: string;
  onSearchChange?: (term: string) => void; // Add callback for search changes
}

export const HydrationPlanTable: React.FC<HydrationPlanTableProps> = ({
  hydrationPlan,
  selectedNodes,
  currentlyHydrating,
  completedNodes,
  failedNodes,
  nodeStatuses,
  nodeErrors,
  isAutoHydrating,
  manifest,
  onSelectNode,
  onHydrateNode,
  onSelectAll,  // Add this prop
  onClearSelection,  // Add this prop
  maxHeight = 'calc(100vh - 350px)',
  onFocusNodeInGraph,
  isRootNodeView,  // Add this to the destructuring
  searchTerm = ''
}) => {
  // Create a custom selectAll function that selects all nodes in the table
  const handleSelectAll = () => {
    console.log("HydrationPlanTable: handleSelectAll called");
    
    // Get all node IDs from the sorted nodes
    const allNodeIds = sortedNodes.map(node => node.id);
    console.log("HydrationPlanTable: Selecting all nodes:", allNodeIds);
    
    // Directly select each node individually
    allNodeIds.forEach(nodeId => {
      if (!selectedNodes.includes(nodeId)) {
        onSelectNode(nodeId);
      }
    });
  };
  const { darkMode } = useTheme();

  // Disable this logging effect
  /*
  React.useEffect(() => {
    if (hydrationPlan && hydrationPlan.ordered_plan) {
      console.log("HydrationPlanTable received plan with", hydrationPlan.ordered_plan.length, "nodes");
      
      // Log the first node's dependencies to check if they're present
      if (hydrationPlan.ordered_plan.length > 0) {
        const firstNode = hydrationPlan.ordered_plan[0];
        console.log("First node in plan:", firstNode.id, "path:", firstNode.path);
        console.log("First node dependencies:", firstNode.dependencies);
        console.log("First node dependencies type:", typeof firstNode.dependencies);
        console.log("First node dependencies is array:", Array.isArray(firstNode.dependencies));
        console.log("First node dependencies length:", Array.isArray(firstNode.dependencies) ? firstNode.dependencies.length : 'not an array');
        
        // Dump the entire first node for inspection
        console.log("First node complete object:", JSON.stringify(firstNode, null, 2));
      }
    }
  }, []); // Empty dependency array means this only runs once when component mounts
  */

  if (!hydrationPlan) {
    return null;
  }

  // Get nodes from the hydration plan's ordered_plan if available
  const planNodes = hydrationPlan.ordered_plan || [];
  
  // Get all file nodes from the manifest
  const allFileNodes = manifest && manifest.nodes ? 
    Object.values(manifest.nodes)
      .filter(node => {
        console.log("Node:", node.id, "type:", node.type, "path:", node.path);
        return node.type === 'file';
      })
      .map(node => ({
        id: node.id,
        path: node.path,
        status: nodeStatuses[node.id] || node.status,
        dependencies: node.dependencies?.map(depId => {
          const depNode = manifest.nodes[depId];
          return {
            id: depId,
            path: depNode?.path || depId,
            status: nodeStatuses[depId] || depNode?.status || 'ghost'
          };
        }) || []
      }))
    : [];

  // Debug logging to understand what's happening
  console.log("HydrationPlanTable - isRootNodeView:", isRootNodeView);
  console.log("HydrationPlanTable - planNodes.length:", planNodes.length);
  console.log("HydrationPlanTable - allFileNodes.length:", allFileNodes.length);
  console.log("HydrationPlanTable - manifest nodes:", manifest?.nodes ? Object.keys(manifest.nodes).length : 0);
  
  // Check if the focus node is a directory
  const focusNodeIsDirectory = hydrationPlan.focus_node_id && 
    manifest?.nodes[hydrationPlan.focus_node_id]?.type === 'directory';
  
  // Use all file nodes if:
  // 1. We're in root node view, or
  // 2. There are no plan nodes, or
  // 3. The focus node is a directory
  const nodesToDisplay = isRootNodeView || planNodes.length === 0 || focusNodeIsDirectory 
    ? allFileNodes 
    : planNodes;
  
  console.log("HydrationPlanTable - nodesToDisplay.length:", nodesToDisplay.length);

  // Filter nodes based on search term
  const filteredNodes = searchTerm 
    ? nodesToDisplay.filter(node => 
        node.path.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : nodesToDisplay;

  // Sort nodes by dependency order (nodes with fewer dependencies first)
  const sortedNodes = [...filteredNodes].sort((a, b) => {
    // First, prioritize nodes in the ordered plan by their index
    if (planNodes.length > 0) {
      const indexA = planNodes.findIndex(node => node.id === a.id);
      const indexB = planNodes.findIndex(node => node.id === b.id);
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
    }
    
    // Then sort by dependency count
    const depCountA = a.dependencies?.length || 0;
    const depCountB = b.dependencies?.length || 0;
    if (depCountA !== depCountB) {
      return depCountA - depCountB;
    }
    
    // Finally, sort alphabetically by path
    return a.path.localeCompare(b.path);
  });

  if (!sortedNodes.length) {
    return null;
  }

  // Calculate if all nodes are selected
  const allSelected = sortedNodes.length > 0 && 
    sortedNodes.every(node => selectedNodes.includes(node.id));
  
  // Calculate if some nodes are selected
  const someSelected = selectedNodes.length > 0 && !allSelected;
  
  // Debug logging for selection state
  console.log("HydrationPlanTable - Selection state:", {
    allSelected,
    someSelected,
    selectedNodesCount: selectedNodes.length,
    totalNodesCount: sortedNodes.length,
    sortedNodeIds: sortedNodes.map(node => node.id),
    selectedNodeIds: selectedNodes
  });

  // Debug logging for the selection functions
  console.log("HydrationPlanTable - onSelectAll function type:", typeof onSelectAll);
  console.log("HydrationPlanTable - onClearSelection function type:", typeof onClearSelection);

  return (
    <div className="overflow-y-auto" style={{ maxHeight }}>
      <table className="w-full">
        <thead className={`sticky top-0 z-10 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <tr>
            <th className="p-1 text-left w-10">
              <input
                type="checkbox"
                checked={allSelected}
                ref={input => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                onChange={() => {
                  if (allSelected) {
                    onClearSelection();
                  } else {
                    handleSelectAll();
                  }
                }}
                disabled={isAutoHydrating}
                className="accent-blue-600 h-4 w-4 cursor-pointer"
              />
            </th>
            <th className="p-1 text-left">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span>File Path</span>
                  {/* Search input moved from panel to table header */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => {
                        // We need to handle the search term change here
                        // and pass it up to the parent component
                        if (onSearchChange) {
                          onSearchChange(e.target.value);
                        }
                      }}
                      className={`text-xs border rounded p-1 w-24 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                    {searchTerm && (
                      <button 
                        className="absolute right-1 top-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        onClick={() => onSearchChange && onSearchChange('')}
                      >
                        <FiX size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                    }`}
                    onClick={handleSelectAll}
                    title="Select all nodes for hydration"
                    disabled={isAutoHydrating}
                  >
                    Select All
                  </button>
                  <button
                    className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                    }`}
                    onClick={onClearSelection}
                    title="Clear node selection"
                    disabled={isAutoHydrating}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </th>
            <th className="p-1 text-left w-24">Status</th>
            <th className="p-1 text-left">Dependencies</th>
            <th className="p-1 text-left w-24">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {sortedNodes.map((node: any) => (
            <HydrationNodeRow
              key={node.id}
              node={node}
              isSelected={selectedNodes.includes(node.id)}
              isCurrentlyHydrating={currentlyHydrating === node.id}
              isCompleted={completedNodes.has(node.id)}
              isFailed={failedNodes.has(node.id)}
              errorMessage={nodeErrors[node.id]}
              nodeStatuses={nodeStatuses}
              completedNodes={completedNodes}
              onSelect={() => onSelectNode(node.id)}
              onHydrate={() => onHydrateNode(node.id)}
              isAutoHydrating={isAutoHydrating}
              isValid={nodePathResolver.isValid(node.id, manifest)}
              isFocusNode={node.id === hydrationPlan.focus_node_id}
              onFocusInGraph={onFocusNodeInGraph} // Pass the focus callback
            />
          ))}
        </tbody>
      </table>
      
      {/* Show message when no results match search */}
      {sortedNodes.length === 0 && searchTerm && (
        <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
          No files match your search term "{searchTerm}"
        </div>
      )}
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'ghost': return 'text-gray-400';
    case 'pending': return 'text-yellow-500';
    case 'hydrating': return 'text-blue-500';
    case 'fresh': return 'text-green-500';
    case 'stale': return 'text-orange-500';
    case 'locked': return 'text-red-500';
    default: return 'text-gray-400';
  }
};
